import React, { useState, useEffect } from "react";
import { useNavigate, Link } from "react-router-dom";
import api, { formatApiError } from "@/lib/api";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import { buscarCep } from "@/lib/cep";
import { useAuth } from "@/contexts/AuthContext";
import { DocumentosMontador } from "@/components/DocumentosMontador";
import { cpfValido, telefoneValido } from "@/lib/validacao-cadastro";

const DIAS = ["seg","ter","qua","qui","sex","sab"];
const LIMITE_SEMANAL_PLATAFORMA = 12;

export default function CadastroMontador() {
  const navigate = useNavigate();
  const { login } = useAuth();
  const [montadoras, setMontadoras] = useState([]);
  const [step, setStep] = useState(1);
  const [loading, setLoading] = useState(false);
  const [cidadesSel, setCidadesSel] = useState([]);
  const [municipiosCad, setMunicipiosCad] = useState([]);
  const [buscaCidadeAt, setBuscaCidadeAt] = useState("");
  const [bairrosPorCidade, setBairrosPorCidade] = useState({}); // {cidade: [bairros]}
  const [bairrosDispCad, setBairrosDispCad] = useState({}); // {cidade: {loading, lista}}
  const [buscaBairroCad, setBuscaBairroCad] = useState({});
  const [form, setForm] = useState({
    nome: "", cpf: "", rg: "", data_nascimento: "",
    cep: "", rua: "", numero: "", complemento: "", bairro: "",
    cidade: "", estado: "", pais: "Brasil",
    telefone: "", whatsapp: "", email: "", password: "",
    tem_veiculo: false, tipo_veiculo: "",
    cidades_atendimento: "", areas_atendimento: "",
    disponibilidade_semanal: [],
    capacidade_diaria: 3, capacidade_semanal: 15,
    pix: "", banco: "", agencia: "", conta: "", tipo_conta: "",
    cnpj_status: "sem_cnpj", cnpj_ativo: false, cnpj: "",
    montadora_id: "",
  });

  const upd = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const [buscandoCep, setBuscandoCep] = useState(false);
  const onCepChange = async (v) => {
    upd("cep", v);
    const limpo = v.replace(/\D/g, "");
    if (limpo.length !== 8) return;
    setBuscandoCep(true);
    const end = await buscarCep(limpo);
    setBuscandoCep(false);
    if (end) {
      setForm((f) => ({
        ...f, rua: end.rua, bairro: end.bairro, cidade: end.cidade, estado: end.estado,
      }));
      setCidadesSel((c) => (c.length ? c : [end.cidade]));
      api.get(`/localidades/${end.estado}/municipios`).then(r => setMunicipiosCad(r.data)).catch(() => {});
      toast.success(`Endereço encontrado: ${end.cidade}/${end.estado}`);
    } else {
      setForm((f) => ({ ...f, rua: "", bairro: "", cidade: "", estado: "" }));
      toast.info("CEP não encontrado — preencha o endereço manualmente");
    }
  };

  // Capacidade semanal = dias selecionados × capacidade diária (limite de distribuição: 12/semana)
  const capSemanalCalc = form.disponibilidade_semanal.length * (Number(form.capacidade_diaria) || 0);
  useEffect(() => {
    setForm((f) => ({ ...f, capacidade_semanal: capSemanalCalc }));
  }, [capSemanalCalc]);

  const toggleDia = (d) => {
    setForm((f) => ({
      ...f,
      disponibilidade_semanal: f.disponibilidade_semanal.includes(d)
        ? f.disponibilidade_semanal.filter((x) => x !== d)
        : [...f.disponibilidade_semanal, d],
    }));
  };

  const carregarBairrosCad = (cidade) => {
    if (!form.estado || bairrosDispCad[cidade]) return;
    setBairrosDispCad(b => ({ ...b, [cidade]: { loading: true, lista: [] } }));
    api.get(`/localidades/${form.estado}/${encodeURIComponent(cidade)}/bairros`)
      .then(r => setBairrosDispCad(b => ({ ...b, [cidade]: { loading: false, lista: r.data.bairros || [] } })))
      .catch(() => setBairrosDispCad(b => ({ ...b, [cidade]: { loading: false, lista: [] } })));
  };

  const toggleBairroCad = (cidade, bairro) => {
    setBairrosPorCidade(prev => {
      const atual = prev[cidade] || [];
      return { ...prev, [cidade]: atual.includes(bairro) ? atual.filter(x => x !== bairro) : [...atual, bairro] };
    });
  };

  const addCidadeAt = (nome) => {
    const n = (nome || "").trim();
    if (!n) return;
    if (municipiosCad.length > 0 && !municipiosCad.includes(n)) {
      toast.error("Selecione uma cidade da lista para evitar erro de grafia");
      return;
    }
    if (!cidadesSel.includes(n)) { setCidadesSel([...cidadesSel, n]); carregarBairrosCad(n); }
    setBuscaCidadeAt("");
  };

  // Trava de avanço: cada etapa exige os obrigatórios preenchidos e válidos
  const validarEtapa = (etapa) => {
    if (etapa === 1) {
      const faltando = [[form.nome, "Nome completo"], [form.cpf, "CPF"], [form.cidade, "Cidade"], [form.estado, "Estado (UF)"]]
        .filter(([v]) => !String(v || "").trim()).map(([, l]) => l);
      if (faltando.length) return `Preencha para avançar: ${faltando.join(", ")}`;
      if (!cpfValido(form.cpf)) return "CPF inválido — confira os 11 dígitos e os dígitos verificadores";
    }
    if (etapa === 2) {
      const faltando = [[form.telefone, "Telefone"], [form.whatsapp, "WhatsApp"], [form.email, "Email"], [form.password, "Senha"]]
        .filter(([v]) => !String(v || "").trim()).map(([, l]) => l);
      if (faltando.length) return `Preencha para avançar: ${faltando.join(", ")}`;
      if (!telefoneValido(form.telefone)) return "Telefone inválido — use DDD + número (ex.: 11 98888-7777)";
      if (!telefoneValido(form.whatsapp)) return "WhatsApp inválido — use DDD + número (ex.: 11 98888-7777)";
      if (!/^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(form.email)) return "E-mail inválido";
      if ((form.password || "").length < 6) return "Senha deve ter no mínimo 6 caracteres";
    }
    if (etapa === 3 && cidadesSel.length === 0) return "Selecione ao menos 1 cidade de atendimento para avançar";
    return null;
  };

  const avancar = () => {
    const erro = validarEtapa(step);
    if (erro) { toast.error(erro); return; }
    setStep((s) => s + 1);
  };

  const submit = async () => {
    for (const et of [1, 2, 3]) {
      const erro = validarEtapa(et);
      if (erro) { toast.error(erro); setStep(et); return; }
    }
    setLoading(true);
    try {
      const payload = {
        ...form,
        cidades_atendimento: cidadesSel,
        bairros_por_cidade: Object.fromEntries(
          Object.entries(bairrosPorCidade).filter(([c, bs]) => cidadesSel.includes(c) && bs.length > 0)),
        areas_atendimento: form.areas_atendimento.split(",").map(s => s.trim()).filter(Boolean),
        capacidade_diaria: Number(form.capacidade_diaria),
        capacidade_semanal: Number(form.capacidade_semanal),
        cnpj_ativo: form.cnpj_status === "ativo",
        cnpj: form.cnpj_status === "sem_cnpj" ? "" : form.cnpj,
      };
      delete payload.montadora_id_empty;
      if (!payload.montadora_id) delete payload.montadora_id;
      await api.post("/auth/register-montador", payload);
      try {
        await login(form.email, form.password);
        toast.success("Cadastro criado! Agora envie seus documentos e comprovantes.");
        setStep(5);
      } catch {
        toast.success("Cadastro realizado. Faça login para enviar seus documentos.");
        navigate("/login");
      }
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-white">
      <header className="border-b border-border">
        <div className="max-w-3xl mx-auto px-6 py-5 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-3">
            <div className="w-7 h-7 brand-bg" />
            <span className="font-heading font-extrabold">MontaJá</span>
          </Link>
          <Link to="/login" className="text-sm font-medium hover:underline">Já tenho conta</Link>
        </div>
      </header>

      <div className="max-w-3xl mx-auto px-6 py-12">
        <div className="label-cap">Cadastro Público</div>
        <h1 className="font-heading font-extrabold text-4xl tracking-tighter mt-2">Seja um Montador MontaJá</h1>
        <p className="text-muted-foreground mt-3">Etapa {step} de 5</p>

        {/* Progress */}
        <div className="flex gap-1 mt-6 mb-10">
          {[1,2,3,4,5].map((s) => (
            <div key={s} className={`h-1 flex-1 ${s <= step ? "brand-bg" : "bg-border"}`} />
          ))}
        </div>

        {step === 1 && (
          <div className="space-y-5" data-testid="step-pessoais">
            <h2 className="font-heading font-bold text-xl">Dados pessoais</h2>
            <Field label="Nome completo" v={form.nome} onChange={(v) => upd("nome", v)} req testid="cad-nome" />
            <div className="grid md:grid-cols-2 gap-4">
              <Field label="CPF" v={form.cpf} onChange={(v) => upd("cpf", v)} req testid="cad-cpf" />
              <Field label="RG" v={form.rg} onChange={(v) => upd("rg", v)} testid="cad-rg" />
            </div>
            <Field label="Data de nascimento" v={form.data_nascimento} onChange={(v) => upd("data_nascimento", v)} type="date" testid="cad-nasc" />
            <div className="grid md:grid-cols-3 gap-4">
              <div>
                <Label className="label-cap">CEP{buscandoCep && " · buscando…"}</Label>
                <Input value={form.cep} placeholder="00000-000"
                  onChange={(e) => onCepChange(e.target.value)}
                  className="mt-2" data-testid="cad-cep" />
                <p className="text-[11px] text-muted-foreground mt-1">Preenche o endereço automaticamente</p>
              </div>
              <Field label="Rua" v={form.rua} onChange={(v) => upd("rua", v)} testid="cad-rua" />
              <Field label="Número" v={form.numero} onChange={(v) => upd("numero", v)} testid="cad-num" />
            </div>
            <div className="grid md:grid-cols-3 gap-4">
              <Field label="Bairro" v={form.bairro} onChange={(v) => upd("bairro", v)} testid="cad-bairro" />
              <Field label="Cidade" v={form.cidade} onChange={(v) => upd("cidade", v)} req testid="cad-cidade" />
              <Field label="Estado (UF)" v={form.estado} onChange={(v) => upd("estado", v)} req testid="cad-uf" />
            </div>
          </div>
        )}

        {step === 2 && (
          <div className="space-y-5" data-testid="step-contato">
            <h2 className="font-heading font-bold text-xl">Contato e acesso</h2>
            <div className="grid md:grid-cols-2 gap-4">
              <Field label="Telefone" v={form.telefone} onChange={(v) => upd("telefone", v)} req testid="cad-tel" />
              <Field label="WhatsApp" v={form.whatsapp} onChange={(v) => upd("whatsapp", v)} req testid="cad-wpp" />
            </div>
            <Field label="Email" v={form.email} onChange={(v) => upd("email", v)} type="email" req testid="cad-email" />
            <Field label="Senha (mín. 6)" v={form.password} onChange={(v) => upd("password", v)} type="password" req testid="cad-senha" />
          </div>
        )}

        {step === 3 && (
          <div className="space-y-5" data-testid="step-operacional">
            <h2 className="font-heading font-bold text-xl">Operacional</h2>
            <div className="flex items-center gap-3">
              <Checkbox id="veic" checked={form.tem_veiculo} onCheckedChange={(v) => upd("tem_veiculo", !!v)} data-testid="cad-tem-veiculo" />
              <Label htmlFor="veic">Tenho veículo próprio</Label>
            </div>
            {form.tem_veiculo && (
              <Field label="Tipo de veículo" v={form.tipo_veiculo} onChange={(v) => upd("tipo_veiculo", v)} testid="cad-tipo-veic" />
            )}
            <div>
              <Label className="label-cap">Cidades de atendimento *</Label>
              <p className="text-[11px] text-muted-foreground mt-1 mb-2">
                Selecione da lista oficial (sem digitação livre, para evitar erro de grafia).
                {!form.estado && " Informe o CEP/UF na etapa 1 para carregar as cidades."}
              </p>
              <div className="flex gap-2">
                <Input value={buscaCidadeAt} list="cad-lista-cidades"
                  onChange={(e) => setBuscaCidadeAt(e.target.value)}
                  onKeyDown={(e) => e.key === "Enter" && (e.preventDefault(), addCidadeAt(buscaCidadeAt))}
                  onFocus={() => { if (municipiosCad.length === 0 && form.estado) api.get(`/localidades/${form.estado}/municipios`).then(r => setMunicipiosCad(r.data)).catch(() => {}); }}
                  placeholder="Buscar cidade…" data-testid="cad-cidades" />
                <Button type="button" variant="outline" className="btn-sharp"
                  onClick={() => addCidadeAt(buscaCidadeAt)} data-testid="cad-cidades-add">+ Adicionar</Button>
              </div>
              <datalist id="cad-lista-cidades">
                {municipiosCad.map(m => <option key={m} value={m} />)}
              </datalist>
              <div className="space-y-2 mt-2">
                {cidadesSel.map(c => {
                  const sel = bairrosPorCidade[c] || [];
                  const disp = bairrosDispCad[c];
                  const busca = buscaBairroCad[c] || "";
                  const lista = disp?.lista || [];
                  const filtrados = busca ? lista.filter(b => b.toLowerCase().includes(busca.toLowerCase())) : lista;
                  return (
                    <div key={c} className="border border-border p-3" data-testid={`cad-cidade-bloco-${c}`}>
                      <div className="flex items-center justify-between gap-2">
                        <span className="bg-secondary px-2 py-1 text-xs font-bold flex items-center gap-1" data-testid={`cad-cidade-chip-${c}`}>
                          {c}
                          <button type="button" onClick={() => setCidadesSel(cidadesSel.filter(x => x !== c))}
                            className="text-muted-foreground hover:text-foreground">×</button>
                        </span>
                        <span className="text-[10px] font-bold uppercase tracking-wider text-muted-foreground">
                          {sel.length === 0 ? "Todos os bairros" : `${sel.length} bairro(s) selecionado(s)`}
                        </span>
                      </div>
                      {disp?.loading && <div className="mt-2 text-[11px] text-muted-foreground animate-pulse">Carregando bairros de {c}…</div>}
                      {!disp && (
                        <button type="button" className="mt-2 text-[11px] underline text-muted-foreground"
                          onClick={() => carregarBairrosCad(c)}>Escolher bairros específicos</button>
                      )}
                      {disp && !disp.loading && lista.length > 0 && (
                        <div className="mt-2">
                          <div className="flex items-center gap-2 flex-wrap">
                            <Input value={busca} onChange={(e) => setBuscaBairroCad(b => ({ ...b, [c]: e.target.value }))}
                              placeholder={`Buscar bairro em ${c}…`} className="h-7 text-[11px] max-w-52"
                              data-testid={`cad-bairros-busca-${c}`} />
                            <button type="button" className="text-[10px] underline text-muted-foreground"
                              onClick={() => setBairrosPorCidade(p => ({ ...p, [c]: [] }))}>Todos os bairros</button>
                          </div>
                          <div className="mt-2 max-h-36 overflow-auto flex flex-wrap gap-1">
                            {filtrados.map(b => {
                              const on = sel.includes(b);
                              return (
                                <button key={b} type="button" onClick={() => toggleBairroCad(c, b)}
                                  className={`px-2 py-0.5 text-[10px] font-semibold transition-colors ${on
                                    ? "bg-[#142766] text-white" : "border border-border text-muted-foreground hover:border-[#142766]"}`}
                                  data-testid={`cad-bairro-${c}-${b}`}>
                                  {b}
                                </button>
                              );
                            })}
                          </div>
                          <p className="text-[10px] text-muted-foreground mt-1">
                            Nenhum selecionado = recebe montagens em <strong>todos os bairros</strong> de {c}.
                          </p>
                        </div>
                      )}
                      {disp && !disp.loading && lista.length === 0 && (
                        <p className="mt-2 text-[10px] text-muted-foreground">Lista de bairros indisponível — você receberá montagens em toda a cidade.</p>
                      )}
                    </div>
                  );
                })}
              </div>
            </div>
            <Field label="Áreas/Regiões (separadas por vírgula)"
                   v={form.areas_atendimento} onChange={(v) => upd("areas_atendimento", v)} testid="cad-areas" />
            <div>
              <Label className="label-cap">Disponibilidade semanal</Label>
              <div className="flex gap-2 mt-2">
                {DIAS.map(d => (
                  <button key={d} type="button" onClick={() => toggleDia(d)}
                    data-testid={`cad-dia-${d}`}
                    className={`btn-sharp h-10 px-4 text-xs uppercase tracking-wider ${
                      form.disponibilidade_semanal.includes(d)
                        ? "brand-bg text-white border-transparent"
                        : "border-border text-foreground hover:bg-secondary"
                    }`}>
                    {d}
                  </button>
                ))}
              </div>
            </div>
            <div className="grid md:grid-cols-2 gap-4">
              <Field label="Capacidade diária (atendimentos por dia)" v={form.capacidade_diaria} onChange={(v) => upd("capacidade_diaria", v)} type="number" testid="cad-cap-dia" />
              <div>
                <Label className="label-cap">Capacidade semanal (automático)</Label>
                <Input type="number" value={capSemanalCalc} readOnly disabled
                  className="mt-2 bg-secondary font-mono font-bold" data-testid="cad-cap-sem" />
                <p className="text-[11px] text-muted-foreground mt-1">
                  {form.disponibilidade_semanal.length} dia(s) × {Number(form.capacidade_diaria) || 0} por dia = <strong>{capSemanalCalc}</strong>
                  {capSemanalCalc > LIMITE_SEMANAL_PLATAFORMA && (
                    <span className="block text-[#B45309] font-semibold mt-0.5">
                      ⚠ A distribuição automática da plataforma é limitada a {LIMITE_SEMANAL_PLATAFORMA} atendimentos/semana.
                    </span>
                  )}
                </p>
              </div>
            </div>
          </div>
        )}

        {step === 4 && (
          <div className="space-y-5" data-testid="step-financeiro">
            <h2 className="font-heading font-bold text-xl">Financeiro e fiscal</h2>
            <Field label="Chave PIX" v={form.pix} onChange={(v) => upd("pix", v)} testid="cad-pix" />
            <div className="grid md:grid-cols-3 gap-4">
              <Field label="Banco" v={form.banco} onChange={(v) => upd("banco", v)} testid="cad-banco" />
              <Field label="Agência" v={form.agencia} onChange={(v) => upd("agencia", v)} testid="cad-ag" />
              <Field label="Conta" v={form.conta} onChange={(v) => upd("conta", v)} testid="cad-conta" />
            </div>
            <div>
              <Label className="label-cap">Tipo de conta</Label>
              <Select value={form.tipo_conta} onValueChange={(v) => upd("tipo_conta", v)}>
                <SelectTrigger className="mt-2" data-testid="cad-tipo-conta"><SelectValue placeholder="Selecione" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="corrente">Conta corrente</SelectItem>
                  <SelectItem value="poupanca">Poupança</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="border-t border-border pt-4">
              <Label className="label-cap">Situação do CNPJ</Label>
              <div className="mt-3 grid md:grid-cols-3 gap-2">
                {[
                  ["ativo", "Tenho CNPJ ativo"],
                  ["inativo", "Tenho CNPJ inativo"],
                  ["sem_cnpj", "Não tenho CNPJ"],
                ].map(([val, label]) => (
                  <label key={val}
                    className={`btn-sharp border p-3 cursor-pointer text-sm flex items-center gap-2 ${
                      form.cnpj_status === val ? "brand-bg text-white border-transparent" : "border-border hover:bg-secondary"
                    }`}
                    data-testid={`cad-cnpj-${val}`}>
                    <input type="radio" name="cnpj_status" value={val}
                      checked={form.cnpj_status === val}
                      onChange={() => upd("cnpj_status", val)}
                      className="accent-[#142766]" />
                    {label}
                  </label>
                ))}
              </div>
              {form.cnpj_status !== "sem_cnpj" && (
                <div className="mt-4">
                  <Field label={form.cnpj_status === "inativo" ? "Número do CNPJ (inativo)" : "Número do CNPJ"}
                         v={form.cnpj} onChange={(v) => upd("cnpj", v)} testid="cad-cnpj" />
                </div>
              )}
            </div>
          </div>
        )}

        {step === 5 && (
          <div className="space-y-6" data-testid="step-documentos">
            <div className="border-2 border-[#00A859] bg-[#00A859]/5 p-4 text-sm">
              <strong>✓ Conta criada com sucesso!</strong> Último passo: envie seus documentos e comprovantes
              (documento de identidade, comprovante de residência e selfie) para a análise da equipe.
            </div>
            <DocumentosMontador />
            <Button className="btn-sharp brand-bg text-white w-full h-12"
              onClick={() => navigate("/montador")} data-testid="cad-concluir">
              Concluir — ir para o meu painel
            </Button>
            <p className="text-[11px] text-muted-foreground text-center">
              Você também pode enviar/reenviar documentos depois, pelo seu painel.
            </p>
          </div>
        )}

        {step < 5 && (
        <div className="flex justify-between mt-10 pt-6 border-t border-border">
          <Button variant="outline" className="btn-sharp" onClick={() => setStep(s => Math.max(1, s - 1))}
                  disabled={step === 1} data-testid="cad-voltar">Voltar</Button>
          {step < 4 ? (
            <Button className="btn-sharp brand-bg text-white hover:bg-[#001A5E]"
                    onClick={avancar} data-testid="cad-avancar">Avançar</Button>
          ) : (
            <Button className="btn-sharp brand-bg text-white hover:bg-[#001A5E]"
                    onClick={submit} disabled={loading} data-testid="cad-enviar">
              {loading ? "Enviando…" : "Criar conta e enviar documentos"}
            </Button>
          )}
        </div>
        )}
      </div>
    </div>
  );
}

function Field({ label, v, onChange, type="text", req=false, testid }) {
  return (
    <div>
      <Label className="label-cap">{label}{req && " *"}</Label>
      <Input type={type} value={v} required={req}
        onChange={(e) => onChange(e.target.value)}
        className="mt-2" data-testid={testid} />
    </div>
  );
}
